import { useEffect, useState } from "react";
import {
  Activity,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2,
} from "lucide-react";
import {
  exportAuditReport,
  searchAuditEvents,
  setComplianceContext,
} from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type { AuditEvent } from "../../types/compliance";
import ShipmentCasePicker from "./ShipmentCasePicker";

export default function AuditCenter() {
  const { credentials } = useComplianceAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);

  // Filters
  const [checkType, setCheckType] = useState("");
  const [workflowId, setWorkflowId] = useState("");
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Export
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<"json" | "csv">("json");

  useEffect(() => {
    setComplianceContext(
      credentials?.tenantId ?? "default",
      credentials?.role ?? "COMPLIANCE_ADMIN",
    );
  }, [credentials?.tenantId, credentials?.role]);

  useEffect(() => {
    loadEvents();
  }, [page, selectedCaseId, workflowId]);

  async function loadEvents() {
    setLoading(true);
    try {
      const data = await searchAuditEvents({
        check_type: checkType || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        workflow_id: workflowId || undefined,
        batch_id: selectedCaseId || undefined,
        page,
        page_size: pageSize,
      });
      setEvents(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error("Failed to load audit events", err);
    } finally {
      setLoading(false);
    }
  }

  function handleSearch() {
    setPage(1);
    loadEvents();
  }

  async function handleExport() {
    setExporting(true);
    try {
      const data = await exportAuditReport({
        export_format: exportFormat,
        check_type: checkType || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        workflow_id: workflowId || undefined,
        batch_id: selectedCaseId || undefined,
      });

      // Download the file
      const content =
        exportFormat === "json"
          ? JSON.stringify(data.entries, null, 2)
          : arrayToCsv(data.entries);

      const blob = new Blob([content], {
        type: exportFormat === "json" ? "application/json" : "text/csv",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed", err);
    } finally {
      setExporting(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Activity size={24} className="text-purple-600" />
          <h1 className="text-2xl font-bold text-gray-900">Audit Center</h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as "json" | "csv")}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          >
            <option value="json">JSON</option>
            <option value="csv">CSV</option>
          </select>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm px-4 py-2 rounded-lg flex items-center gap-2 transition"
          >
            {exporting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            Export
          </button>
        </div>
      </div>

      <div className="mb-4">
        <ShipmentCasePicker
          selectedCaseId={selectedCaseId}
          selectedWorkflowId={workflowId || undefined}
          onSelectCase={(shipmentCase, detail) => {
            setSelectedCaseId(shipmentCase.case_id);
            setWorkflowId(detail.documents[0]?.workflow_id ?? "");
            setShowFilters(true);
            setPage(1);
          }}
          onSelectWorkflow={(document) => {
            setWorkflowId(document.workflow_id);
            setShowFilters(true);
            setPage(1);
          }}
        />
        {selectedCaseId && (
          <p className="mt-2 text-xs text-gray-500">
            Active shipment case audit trail: <span className="font-mono">{selectedCaseId}</span>. Select a document workflow above to narrow events and exports to that file.
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4">
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"
        >
          <Filter size={14} />
          Filters {showFilters ? "(hide)" : "(show)"}
        </button>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Check Type
              </label>
              <input
                type="text"
                value={checkType}
                onChange={(e) => setCheckType(e.target.value)}
                placeholder="invoice / vat / ecmr"
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Workflow ID
              </label>
              <input
                type="text"
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
                placeholder="wf_abc..."
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                From Date
              </label>
              <input
                type="datetime-local"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                To Date
              </label>
              <input
                type="datetime-local"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              />
            </div>
          </div>
        )}
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={handleSearch}
            className="bg-gray-800 hover:bg-gray-900 text-white text-sm px-4 py-1.5 rounded-lg flex items-center gap-2 transition"
          >
            <Search size={14} />
            Search
          </button>
          <button
            type="button"
            onClick={() => {
              setCheckType("");
              setWorkflowId("");
              setSelectedCaseId("");
              setFromDate("");
              setToDate("");
              setPage(1);
              loadEvents();
            }}
            className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1.5"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Event count */}
      <div className="text-sm text-gray-500 mb-3">
        {total} event{total !== 1 ? "s" : ""} found
      </div>

      {/* Events table */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Event
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Workflow
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actor
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Details
                </th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Timestamp
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8">
                    <Loader2
                      size={20}
                      className="animate-spin inline-block text-blue-600"
                    />
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    No audit events found.
                  </td>
                </tr>
              ) : (
                events.map((evt) => (
                  <tr key={evt.event_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                        {evt.event_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                      {evt.workflow_id.slice(0, 16)}…
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {evt.actor}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600 line-clamp-1">
                        {JSON.stringify(evt.details).slice(0, 80)}
                        {JSON.stringify(evt.details).length > 80 ? "…" : ""}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {new Date(evt.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <span className="text-xs text-gray-500">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="p-1 text-gray-500 hover:text-gray-700 disabled:text-gray-300"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="p-1 text-gray-500 hover:text-gray-700 disabled:text-gray-300"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Simple CSV converter
function arrayToCsv(entries: Record<string, unknown>[]): string {
  if (entries.length === 0) return "";
  const headers = Object.keys(entries[0]);
  const lines = [headers.join(",")];
  for (const entry of entries) {
    lines.push(
      headers
        .map((h) => {
          const val = entry[h];
          if (val === null || val === undefined) return "";
          const str = String(val);
          return str.includes(",") || str.includes('"')
            ? `"${str.replace(/"/g, '""')}"`
            : str;
        })
        .join(","),
    );
  }
  return lines.join("\n");
}
